import { useCallback, useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';
import { DRAG_SEND_INTERVAL_MS } from '@sincronia/shared';

export type DragState = { active: boolean; overDrop: boolean };

export type UseCardDragOptions = {
  enabled: boolean;
  /** Área de referência para normalizar as coordenadas (0–1). */
  stageRef: RefObject<HTMLElement | null>;
  /** Zona central de soltura. */
  dropRef: RefObject<HTMLElement | null>;
  /** Elemento arrastado (a transformação é aplicada direto no DOM). */
  cardRef: RefObject<HTMLElement | null>;
  onStart: (x: number, y: number) => void;
  onMove: (x: number, y: number) => void;
  onDrop: () => void;
  onCancel: () => void;
};

export type CardDragHandlers = {
  state: DragState;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  /** Verdadeiro quando o ponteiro andou o suficiente para ser um arrasto. */
  movedRef: RefObject<boolean>;
};

const MOVE_THRESHOLD_PX = 6;

/**
 * Arrasto com Pointer Events e pointer capture.
 *
 * A carta acompanha o dedo localmente a 60 fps (transformações escritas
 * diretamente no DOM); pela rede enviamos posições normalizadas coalescidas em
 * ~15 Hz. A API HTML5 Drag and Drop não é usada: no celular ela é irregular.
 */
export function useCardDrag(options: UseCardDragOptions): CardDragHandlers {
  const { enabled, stageRef, dropRef, cardRef, onStart, onMove, onDrop, onCancel } = options;

  const [state, setState] = useState<DragState>({ active: false, overDrop: false });
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const deltaRef = useRef({ dx: 0, dy: 0 });
  const frameRef = useRef<number | null>(null);
  const lastSentRef = useRef(0);
  const movedRef = useRef(false);
  const overDropRef = useRef(false);

  const normalize = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const stage = stageRef.current;
      if (!stage) return { x: 0.5, y: 0.5 };
      const rect = stage.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return { x: 0.5, y: 0.5 };
      return {
        x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
        y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
      };
    },
    [stageRef],
  );

  const isOverDrop = useCallback(
    (clientX: number, clientY: number): boolean => {
      const drop = dropRef.current;
      if (!drop) return false;
      const rect = drop.getBoundingClientRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    },
    [dropRef],
  );

  const applyTransform = useCallback(() => {
    frameRef.current = null;
    const element = cardRef.current;
    if (!element) return;
    const { dx, dy } = deltaRef.current;
    element.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(1.06)`;
  }, [cardRef]);

  const resetTransform = useCallback(
    (animated: boolean) => {
      const element = cardRef.current;
      if (!element) return;
      element.style.transition = animated ? 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)' : '';
      element.style.transform = '';
      if (animated) {
        window.setTimeout(() => {
          if (cardRef.current) cardRef.current.style.transition = '';
        }, 240);
      }
    },
    [cardRef],
  );

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  const finish = useCallback(
    (event: PointerEvent<HTMLElement>, forceCancel: boolean) => {
      if (!originRef.current) return;
      originRef.current = null;

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }

      const dropped = !forceCancel && movedRef.current && isOverDrop(event.clientX, event.clientY);
      resetTransform(true);
      overDropRef.current = false;
      setState({ active: false, overDrop: false });

      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // O ponteiro já pode ter sido liberado.
      }

      if (dropped) onDrop();
      else onCancel();
    },
    [isOverDrop, onCancel, onDrop, resetTransform],
  );

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      originRef.current = { x: event.clientX, y: event.clientY };
      deltaRef.current = { dx: 0, dy: 0 };
      movedRef.current = false;
      lastSentRef.current = 0;
      overDropRef.current = false;
      setState({ active: true, overDrop: false });

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Sem captura: o arrasto ainda funciona enquanto o ponteiro estiver sobre a carta.
      }

      const position = normalize(event.clientX, event.clientY);
      onStart(position.x, position.y);
    },
    [enabled, normalize, onStart],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      const origin = originRef.current;
      if (!origin) return;

      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      deltaRef.current = { dx, dy };

      if (!movedRef.current && Math.hypot(dx, dy) > MOVE_THRESHOLD_PX) movedRef.current = true;

      if (frameRef.current === null) frameRef.current = requestAnimationFrame(applyTransform);

      const over = isOverDrop(event.clientX, event.clientY);
      if (over !== overDropRef.current) {
        overDropRef.current = over;
        setState({ active: true, overDrop: over });
      }

      const now = performance.now();
      if (now - lastSentRef.current >= DRAG_SEND_INTERVAL_MS) {
        lastSentRef.current = now;
        const position = normalize(event.clientX, event.clientY);
        onMove(position.x, position.y);
      }
    },
    [applyTransform, isOverDrop, normalize, onMove],
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => finish(event, false),
    [finish],
  );

  const onPointerCancel = useCallback(
    (event: PointerEvent<HTMLElement>) => finish(event, true),
    [finish],
  );

  return { state, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, movedRef };
}
